import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Calendar,
  Gear,
  ArrowRight,
  ArrowUpRight,
  Lock,
  ChatCircleDots,
  ShareNetwork,
  Factory,
  ClipboardText,
  Kanban,
  BookOpen,
  Barcode,
  Sparkle,
  Storefront,
  Wrench,
  Armchair,
  UserCircle,
  Vault,
  Files,
} from '@phosphor-icons/react';
import { useAuth, canSeeSettings, canUseApp } from '@/auth/AuthContext';
import AccountMenu from '@/components/layout/AccountMenu';
import LanguageToggle from '@/components/layout/LanguageToggle';
import { usePlannerAlerts } from '@/hooks/usePlannerAlerts';
import { useT } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { mktApi } from '@/pages/marketing/marketingApi';

/**
 * Sensbey Web App — top-level app launcher.
 * Apps with `section: 'assets'` live inside the 資産管理 folder tile
 * (iOS-folder style: click the tile → dialog with the member apps).
 * マイデバイス stays in the main grid for quick access.
 */
const APPS = [
  {
    id: 'kintai',
    label: '勤怠管理',
    desc: '残業・休暇・勤務時間・出張の各種申請',
    to: '/forms',
    Icon: Calendar,
    bg: 'from-teal-600 to-teal-700',
    glow: 'shadow-blue-200',
    active: true,
  },
  {
    id: 'chat',
    label: 'チャット',
    desc: 'チームでリアルタイムに会話・相談',
    to: '/chat',
    Icon: ChatCircleDots,
    bg: 'from-emerald-500 to-teal-600',
    glow: 'shadow-emerald-200',
    active: true,
  },
  {
    id: 'mfg',
    label: '製造管理',
    desc: '在庫・ロット・倉庫・バーコード管理',
    to: '/mfg',
    Icon: Factory,
    bg: 'from-orange-500 to-amber-600',
    glow: 'shadow-orange-200',
    active: true,
  },
  {
    id: 'shizai',
    label: '資材',
    desc: '発注・仕入先・購買履歴・入荷',
    to: '/shizai',
    Icon: ClipboardText,
    bg: 'from-rose-500 to-pink-600',
    glow: 'shadow-rose-200',
    active: true,
  },
  {
    id: 'parts_request',
    label: '部品受注入力',
    desc: '必要な部品を資材担当へ依頼',
    to: '/parts-request',
    Icon: ClipboardText,
    bg: 'from-orange-500 to-rose-600',
    glow: 'shadow-orange-200',
    active: true,
  },
  {
    id: 'marketing',
    label: '営業',
    desc: '見積・受注・出荷・売上確定の一元管理',
    to: '/marketing',
    Icon: Storefront,
    bg: 'from-fuchsia-600 to-violet-600',
    glow: 'shadow-fuchsia-200',
    active: true,
  },
  {
    id: 'engineering',
    label: '技術',
    desc: '技術資産・設備・データ収集',
    to: '/engineering',
    Icon: Wrench,
    bg: 'from-teal-500 to-cyan-600',
    glow: 'shadow-teal-200',
    active: true,
  },
  {
    id: 'planner',
    label: 'プロジェクト',
    desc: '部署別のプロジェクト・タスク・ガント',
    to: '/planner',
    Icon: Kanban,
    bg: 'from-indigo-500 to-blue-600',
    glow: 'shadow-indigo-200',
    active: true,
  },
  {
    id: 'knowledge',
    label: 'ナレッジ',
    desc: '手順書・マニュアル・議事録などの社内ナレッジ',
    to: '/knowledge',
    Icon: BookOpen,
    bg: 'from-sky-500 to-blue-600',
    glow: 'shadow-sky-200',
    active: true,
  },
  {
    id: 'scanner',
    label: 'スキャナー',
    desc: 'バーコードで入庫・出庫・貸出・棚卸',
    to: '/scanner',
    Icon: Barcode,
    bg: 'from-cyan-500 to-teal-600',
    glow: 'shadow-cyan-200',
    active: true,
  },
  {
    id: 'myit',
    label: 'マイデバイス',
    desc: '自分に割り当てられたPC・ライセンス・メールを確認',
    to: '/my-it',
    Icon: UserCircle,
    bg: 'from-blue-500 to-sky-600',
    glow: 'shadow-blue-200',
    active: true,
    alwaysVisible: true,
  },
  {
    id: 'settings',
    label: '設定',
    desc: '署名・部署・各種設定',
    to: '/settings',
    Icon: Gear,
    bg: 'from-zinc-500 to-zinc-700',
    glow: 'shadow-zinc-300',
    active: true,
    requiresSettings: true,
  },
  // ── 資産管理 folder ──
  {
    id: 'it',
    label: 'ITシステム管理',
    desc: 'VLAN・IP管理、備品・ライセンス・申請',
    to: '/it',
    Icon: ShareNetwork,
    bg: 'from-violet-500 to-indigo-600',
    glow: 'shadow-violet-200',
    active: true,
    section: 'assets',
  },
  {
    id: 'bihin',
    label: '備品管理',
    desc: 'オフィス備品・消耗品・貸出・フロアマップ',
    to: '/bihin',
    Icon: Armchair,
    bg: 'from-green-600 to-emerald-700',
    glow: 'shadow-green-200',
    active: true,
    section: 'assets',
  },
  {
    id: 'docs',
    label: 'ドキュメント',
    desc: '文書管理 — 図面・マニュアル・記録の保管と版管理',
    to: '/documents',
    Icon: Files,
    bg: 'from-teal-600 to-emerald-700',
    glow: 'shadow-teal-200',
    active: true,
  },
];

export default function Launcher() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useT();
  const plannerAlerts = usePlannerAlerts(user);
  const canSettings = canSeeSettings(user);
  const visibleApps = APPS.filter((a) => (!a.requiresSettings || canSettings) && (a.alwaysVisible || canUseApp(user, a.id)));
  const mainApps = visibleApps.filter((a) => a.section !== 'assets');
  const assetApps = visibleApps.filter((a) => a.section === 'assets');
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (!user || !canUseApp(user, 'marketing')) return;
    mktApi.demoOnboarding()
      .then((r) => { if (r.should_prompt) setShowWelcome(true); })
      .catch(() => {});
  }, [user]);

  const ackWelcome = (action) => {
    setShowWelcome(false);
    mktApi.demoOnboardingAck(action).catch(() => {});
    if (action === 'open') navigate('/demo');
  };

  // Shared card renderer (main grid + inside the 資産管理 folder dialog).
  const renderApp = ({ id, to, Icon, bg, active }) => {
    const isActive = !!active;
    // iter_152 · Batch 2 — clear the mfg/scanner department cache on
    // navigation, because the user is starting the app fresh from the
    // launcher and expects the "pick a department" screen.
    const clearAppState = () => {
      if (id === 'mfg' || id === 'scanner') {
        try { localStorage.removeItem('mfg_active_department'); } catch (_e) { /* ignore */ }
      }
    };
    const onLinkClick = () => { clearAppState(); };
    const cardCls = `group relative h-full text-left bg-white rounded-lg border border-zinc-200 p-4 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2 ${
      isActive ? 'cursor-pointer hover:border-teal-500 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99]' : 'cursor-not-allowed opacity-70'
    } shadow-sm block no-underline text-inherit`;

    const CardBody = (
      <>
        {!isActive && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 text-[10px] font-semibold uppercase tracking-wider">
            <Lock size={10} weight="bold" />
            {t('common.comingSoon')}
          </span>
        )}
        <div className={`relative flex items-center justify-center aspect-square w-12 rounded-lg bg-gradient-to-br ${bg} text-white shadow-sm ring-1 ring-black/5 mb-3`}>
          <Icon size={24} weight="duotone" />
        </div>
        <h3 className="text-[15px] font-semibold text-zinc-900 mb-1">{t(`app.${id}`)}</h3>
        <p className="text-xs text-zinc-500 leading-relaxed">{t(`launcher.desc.${id}`)}</p>
        {isActive && (
          <div className="mt-3 flex items-center gap-1 text-xs font-medium text-teal-700 opacity-0 group-hover:opacity-100 transition-opacity">
            {t('common.open')} <ArrowRight size={12} weight="bold" />
          </div>
        )}
      </>
    );

    if (!isActive || !to) {
      return (
        <div key={id} data-testid={`app-${id}`} className={cardCls} aria-disabled="true">
          {CardBody}
        </div>
      );
    }
    // Link + corner ↗ icon as SIBLINGS to avoid nested <a> (see iter_152).
    return (
      <div key={id} className="group relative h-full">
        <Link
          to={to}
          data-testid={`app-${id}`}
          onClick={onLinkClick}
          className={cardCls.replace('group relative', 'relative')}
        >
          {CardBody}
        </Link>
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => clearAppState()}
          data-testid={`app-${id}-new-tab`}
          title={t('launcher.openInNewTab')}
          aria-label={t('launcher.openInNewTab')}
          className="absolute top-3 right-3 z-10 h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-teal-700 hover:bg-zinc-100 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
        >
          <ArrowUpRight size={14} weight="bold" />
        </a>
      </div>
    );
  };

  // 資産管理 folder tile — iOS-folder style: mini icons of the apps inside.
  const renderAssetsFolder = () => (
    <button
      key="assets-folder"
      type="button"
      onClick={() => setAssetsOpen(true)}
      data-testid="launcher-assets-folder"
      className="group relative h-full text-left bg-white rounded-lg border border-zinc-200 p-4 transition-all duration-200 cursor-pointer hover:border-teal-500 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
    >
      <div className="relative w-12 aspect-square rounded-lg bg-zinc-100/90 ring-1 ring-black/5 mb-3 p-1.5 grid grid-cols-2 gap-1">
        {assetApps.slice(0, 4).map(({ id, Icon, bg }) => (
          <span key={id} className={`rounded-md bg-gradient-to-br ${bg} text-white flex items-center justify-center`}>
            <Icon size={12} weight="duotone" />
          </span>
        ))}
        {assetApps.length === 1 && <span className="rounded-md bg-zinc-200/60" />}
      </div>
      <h3 className="text-[15px] font-semibold text-zinc-900 mb-1">{t('launcher.section.assets')}</h3>
      <p className="text-xs text-zinc-500 leading-relaxed">{t('launcher.desc.assets_group')}</p>
      <div className="mt-3 flex items-center gap-1 text-xs font-medium text-teal-700 opacity-0 group-hover:opacity-100 transition-opacity">
        {t('common.open')} <ArrowRight size={12} weight="bold" />
      </div>
    </button>
  );

  return (
    <div className="min-h-[calc(100vh-64px)] bg-zinc-50 relative overflow-hidden">

      <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-24">
        {/* Top-left logo */}
        <div className="absolute top-6 left-6 sm:top-8 sm:left-8">
          <img
            src="https://customer-assets.emergentagent.com/job_shift-directive-app/artifacts/62hq2mxj_sensbey%20logo.png"
            alt="Sensbey"
            className="h-5 sm:h-6 w-auto opacity-90"
          />
        </div>

        {/* Top-right account menu */}
        <div className="absolute top-5 right-6 sm:top-7 sm:right-8 z-10 flex items-center gap-3" data-testid="launcher-account-menu">
          <LanguageToggle />
          <AccountMenu user={user} onLogout={logout} plannerAlerts={plannerAlerts} />
        </div>

        <header className="mb-10 text-center">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Sensbey Web App</h1>
          <p className="mt-1 text-xs text-zinc-500 tracking-wide" data-testid="brand-tagline">統合業務プラットフォーム</p>
          <p className="mt-3 text-sm text-zinc-500">{t('launcher.subtitle')}</p>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 stagger-children">
          {mainApps.map(renderApp)}
          {assetApps.length > 0 && renderAssetsFolder()}
        </div>

        <footer className="mt-20 text-center">
          <p className="text-[11px] text-zinc-400">© {new Date().getFullYear()} 株式会社センスビー　·　Sensbey Web App</p>
        </footer>
      </div>

      {/* 資産管理 folder dialog */}
      <Dialog open={assetsOpen} onOpenChange={setAssetsOpen}>
        <DialogContent className="max-w-lg" data-testid="launcher-assets-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Vault size={20} weight="duotone" className="text-violet-600" /> {t('launcher.section.assets')}
            </DialogTitle>
            <DialogDescription>{t('launcher.desc.assets_group')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-1">
            {assetApps.map(renderApp)}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showWelcome} onOpenChange={(v) => !v && ackWelcome('dismiss')}>
        <DialogContent className="max-w-md" data-testid="launcher-welcome-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} weight="fill" className="text-fuchsia-500" /> ようこそ Sensbey Web App へ
            </DialogTitle>
            <DialogDescription className="pt-1 text-left leading-relaxed">
              受注 → 見積書 → 製造オーダー → 資材調達 → 出荷 → 売上確定 → 締め請求・入金・領収書まで、
              仕事の一連の流れを<b>デモモード</b>で体験できます。
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-[11px] text-orange-700">
            デモはあなた専用のテストモード（サンドボックス）で動くため、本番データには一切影響しません。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => ackWelcome('dismiss')} data-testid="launcher-welcome-later-btn">あとで見る</Button>
            <Button onClick={() => ackWelcome('open')} data-testid="launcher-welcome-open-btn">
              <Sparkle size={15} className="mr-1.5" /> デモを体験する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
